/**
 * useDimStyleEditorDialog — Track 3 Phase 3
 *
 * Owns the DimStyleEditorDialog feature extracted from `App.tsx` as part
 * of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`).
 *
 * Responsibilities:
 *   - Owns the `isOpen` flag, `open()` / `close()` callbacks, and the
 *     `editingDimStyleName` state used for "browse other styles" inside
 *     the dialog.
 *   - Resolves the current `style` to edit by looking up the target
 *     name (from `editingDimStyleName` → `propertyDraft["Dimension
 *     style"]` → first style) in
 *     `geometryDocument.styleTables.dimensionStyles`.
 *   - Wires the dialog's onSave / onCreateStyle / onSelectStyle /
 *     onAnnotativePropagation handlers to the corresponding store
 *     actions.
 *   - Closing the dialog also clears `editingDimStyleName` so the next
 *     open re-resolves from the property draft.
 *
 * Returned shape:
 *   {
 *     isOpen,
 *     hasStyles,                       // gate prop bundle availability
 *     props: { style, textStyleNames, dimensionStyleNames, cannoscaleName,
 *              onSave, onCreateStyle, onSelectStyle, onAnnotativePropagation,
 *              onClose },
 *     open,
 *     close,
 *   }
 *
 * The orchestrator renders `<DimStyleEditorDialog>` when both `isOpen`
 * and `hasStyles` are true.
 *
 * AutoCAD parity:
 *   The DIMSTYLE / DDIM commands open the Dimension Style Manager
 *   dialog. The dialog component preserves the full tabbed UI; the hook
 *   only orchestrates open/close, current-style resolution, and store
 *   binding.
 */

import { useCallback, useMemo, useState } from "react";

import { useDialogOpenState } from "./useDialogOpenState";

import type { DimensionStyle, TextStyle } from "@dxf-ai-editor/drawing-model";

import { useEditorStore } from "../state/editorStore";
import type { AnnotativeStylePropagationHandler } from "./styleEditorDialogTypes";

// Phase 1.k (П35Р React #185 fix) — stable sentinel empty arrays so
// the `?? []` fallback in Zustand selectors below does NOT allocate a
// fresh array reference on every store update. A fresh ref each call
// makes Zustand's Object.is bail-out fail → component re-renders on
// every unrelated store mutation (importProgress chunks during a
// 685k-entity import = hundreds of spurious re-renders → React #185).
const EMPTY_DIM_STYLES: DimensionStyle[] = [];
const EMPTY_TEXT_STYLES: TextStyle[] = [];

export interface UseDimStyleEditorDialogParams {
  /**
   * Current property draft map. The hook reads `["Dimension style"]`
   * to pre-select the style currently applied on the selected entity.
   */
  propertyDraft: Record<string, string>;
}

export interface UseDimStyleEditorDialogResult {
  /** Whether to mount `<DimStyleEditorDialog>`. */
  isOpen: boolean;
  /**
   * Whether at least one dimension style exists. The orchestrator
   * should gate dialog mounting on this — the original code did
   * `dimensionStyles?.length > 0` inline.
   */
  hasStyles: boolean;
  /**
   * Props bundle for `<DimStyleEditorDialog>`. Only meaningful when
   * `isOpen && hasStyles`. `style` is the active record; if no style
   * matches the resolved target name, the first style is used.
   */
  props: {
    style: DimensionStyle;
    textStyleNames: string[];
    dimensionStyleNames: string[];
    /**
     * Name of the CURRENT dimension style (AutoCAD DIMSTYLE sysvar) — resolved
     * from `defaultObjectProperties["Dimension style"]`, falling back to the
     * first style. The manager marks it and disables "Set Current" on it.
     */
    currentStyleName: string;
    cannoscaleName: string;
    onSave: (updates: Partial<DimensionStyle>) => void;
    onCreateStyle: (
      name: string,
      basedOn: string | undefined,
    ) => { ok: true } | { ok: false; error: string };
    /** Delete a named style (mirrors MLEADERSTYLE; "Standard" is protected). */
    onDeleteStyle: (
      name: string,
    ) => { ok: true } | { ok: false; error: string };
    /** Rename a named style ("Standard" is protected; refs are re-pointed). */
    onRenameStyle: (
      oldName: string,
      newName: string,
    ) => { ok: true } | { ok: false; error: string };
    /** Make a named style the current DIMSTYLE (new dimensions inherit it). */
    onSetCurrentStyle: (
      name: string,
    ) => { ok: true } | { ok: false; error: string };
    onSelectStyle: (name: string) => void;
    onAnnotativePropagation: AnnotativeStylePropagationHandler;
    onClose: () => void;
  } | null;
  /** Imperative open — used by quickbar / ribbon / properties view. */
  open: () => void;
  /** Imperative close — same as `props.onClose`, exposed for symmetry. */
  close: () => void;
}

export function useDimStyleEditorDialog({
  propertyDraft,
}: UseDimStyleEditorDialogParams): UseDimStyleEditorDialogResult {
  const updateDimensionStyle = useEditorStore(
    (store) => store.updateDimensionStyle,
  );
  const addDimensionStyle = useEditorStore((store) => store.addDimensionStyle);
  const deleteDimensionStyle = useEditorStore(
    (store) => store.deleteDimensionStyle,
  );
  const renameDimensionStyle = useEditorStore(
    (store) => store.renameDimensionStyle,
  );
  const setCurrentDimensionStyle = useEditorStore(
    (store) => store.setCurrentDimensionStyle,
  );
  const recordAnnotativeStylePropagation = useEditorStore(
    (store) => store.recordAnnotativeStylePropagation,
  );
  const dimensionStyles = useEditorStore(
    (store) =>
      store.geometryDocument.styleTables?.dimensionStyles ?? EMPTY_DIM_STYLES,
  );
  // AutoCAD DIMSTYLE sysvar — the current dimension style new dimensions
  // inherit, persisted as the default object property "Dimension style".
  const currentDimStyleDefault = useEditorStore(
    (store) =>
      store.state.propertyWorkflows.defaultObjectProperties["Dimension style"],
  );
  const textStyles = useEditorStore(
    (store) =>
      store.geometryDocument.styleTables?.textStyles ?? EMPTY_TEXT_STYLES,
  );
  const cannoscaleName = useEditorStore(
    (store) => store.geometryDocument.cannoscaleName,
  );

  // Name of the dim style currently being edited in the editor dialog.
  // null = fall back to the currently applied style on the selected
  // dimension. Cleared on close so the next open re-resolves from the
  // property draft (matches original App.tsx:5301 behaviour).
  const [editingDimStyleName, setEditingDimStyleName] = useState<
    string | null
  >(null);

  const { isOpen, open, close } = useDialogOpenState({
    onClose: () => setEditingDimStyleName(null),
  });

  const hasStyles = dimensionStyles.length > 0;

  const currentStyle = useMemo<DimensionStyle | null>(() => {
    if (!hasStyles) return null;
    // @codex P3 — selection fallback chain: the browse-override, then the
    // selected entity's applied style, then the PERSISTED current DIMSTYLE
    // (`currentDimStyleDefault`), and only then the first table entry. Without
    // the `currentDimStyleDefault` rung the no-selection / empty-draft flow
    // (open the manager with nothing selected after `DIMSTYLE SET Arch` / Set
    // Current `Arch`) opened on the first style (usually `Standard`) instead of
    // the persisted current style — the user edited / acted on the wrong style.
    const targetName =
      editingDimStyleName ??
      propertyDraft["Dimension style"] ??
      currentDimStyleDefault ??
      dimensionStyles[0]?.name;
    return (
      dimensionStyles.find((s) => s.name === targetName) ??
      dimensionStyles[0] ??
      null
    );
  }, [
    hasStyles,
    editingDimStyleName,
    propertyDraft,
    currentDimStyleDefault,
    dimensionStyles,
  ]);

  const onSave = useCallback(
    (updates: Partial<DimensionStyle>) => {
      if (currentStyle?.name) {
        updateDimensionStyle(currentStyle.name, updates);
      }
    },
    [currentStyle, updateDimensionStyle],
  );

  const onCreateStyle = useCallback(
    (name: string, basedOn: string | undefined) => {
      const result = addDimensionStyle(name, basedOn);
      return result.ok
        ? ({ ok: true } as const)
        : ({ ok: false, error: result.error } as const);
    },
    [addDimensionStyle],
  );

  // Delete / Set-Current are pass-throughs — the store actions already return the
  // exact `{ ok } | { ok, error }` shape the dialog expects, so they are wired
  // DIRECTLY in the props bundle below (no re-wrapping wrapper). On a delete the
  // deleted name may still be `editingDimStyleName`; `currentStyle` self-heals via
  // its find→first fallback, so no extra reset is needed.
  const onRenameStyle = useCallback(
    (oldName: string, newName: string) => {
      const result = renameDimensionStyle(oldName, newName);
      if (result.ok) {
        // Keep the editor selection on the renamed style.
        setEditingDimStyleName(newName.trim());
        return { ok: true } as const;
      }
      return { ok: false, error: result.error } as const;
    },
    [renameDimensionStyle],
  );

  const onSelectStyle = useCallback((name: string) => {
    setEditingDimStyleName(name);
  }, []);

  const onAnnotativePropagation = useCallback(
    ({ direction }: { direction: "enable" | "disable" }) => {
      if (currentStyle?.name) {
        recordAnnotativeStylePropagation({
          styleKind: "dimstyle",
          styleName: currentStyle.name,
          direction,
          cannoscaleName,
        });
      }
    },
    [currentStyle, recordAnnotativeStylePropagation, cannoscaleName],
  );

  // Resolve the current style name for the "Set Current" marker: the persisted
  // default, else the first style (a drawing always has ≥1 style here).
  const currentStyleName =
    currentDimStyleDefault &&
    dimensionStyles.some((s) => s.name === currentDimStyleDefault)
      ? currentDimStyleDefault
      : (dimensionStyles[0]?.name ?? "Standard");

  const props =
    hasStyles && currentStyle
      ? {
          style: currentStyle,
          textStyleNames: textStyles.map((s) => s.name),
          dimensionStyleNames: dimensionStyles.map((s) => s.name),
          currentStyleName,
          cannoscaleName,
          onSave,
          onCreateStyle,
          onDeleteStyle: deleteDimensionStyle,
          onRenameStyle,
          onSetCurrentStyle: setCurrentDimensionStyle,
          onSelectStyle,
          onAnnotativePropagation,
          onClose: close,
        }
      : null;

  return {
    isOpen,
    hasStyles,
    props,
    open,
    close,
  };
}
